import React, { useState, useEffect } from "react";

const TransferModal = ({
  isOpen,
  onClose,
  onSubmit,
  userBalance,
  transferAmount,
  setTransferAmount,
  transferDescription,
  setTransferDescription,
  selectedUser,
  userSearchTerm,
  onUserSearchChange,
  userSuggestions,
  onUserSelect
}) => {
  const [amountError, setAmountError] = useState("");
  const [userError, setUserError] = useState("");
  const [searchMethod, setSearchMethod] = useState("wallet"); // "name" or "wallet"
  const [walletNumber, setWalletNumber] = useState("");
  const [walletSearchError, setWalletSearchError] = useState("");
  const [isSearchingWallet, setIsSearchingWallet] = useState(false);

  // Clear errors when modal opens - moved before early return
  useEffect(() => {
    if (isOpen) {
      setAmountError("");
      setUserError("");
      setWalletSearchError("");
      setWalletNumber("");
      setSearchMethod("wallet");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Search user by wallet number
  const searchByWalletNumber = async (walletNum) => {
    if (!walletNum || walletNum.length < 9) { // XXXX-XXXX format
      setWalletSearchError("");
      return;
    }

    setIsSearchingWallet(true);
    setWalletSearchError("");

    try {
      const userId = localStorage.getItem("userId") || 1;
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/accounting/search-by-wallet?walletNumber=${encodeURIComponent(walletNum)}&currentUserId=${userId}`
      );
      const data = await response.json();

      if (data.found && data.user) {
        onUserSelect(data.user);
        setWalletSearchError("");
        setUserError("");
      } else {
        setWalletSearchError("Wallet number not found");
        if (selectedUser) {
          onUserSelect(null);
        }
      }
    } catch (error) {
      console.error("Error searching by wallet:", error);
      setWalletSearchError("Error searching for wallet number");
    } finally {
      setIsSearchingWallet(false);
    }
  };

  // Handle wallet number input change
  const handleWalletNumberChange = (e) => {
    let value = e.target.value.toUpperCase();
    
    // Remove any non-alphanumeric characters except dash
    value = value.replace(/[^A-Z0-9\-]/g, '');
    
    // Format as XXXX-XXXX
    if (value.length > 4 && !value.includes('-')) {
      value = value.slice(0, 4) + '-' + value.slice(4, 8);
    }
    
    // Limit to 9 characters (XXXX-XXXX)
    if (value.length > 9) {
      value = value.slice(0, 9);
    }
    
    setWalletNumber(value);
    
    // Search when we have a complete wallet number
    if (value.length === 9 && value.includes('-')) {
      searchByWalletNumber(value);
    } else if (value.length < 9) {
      setWalletSearchError("");
      if (searchMethod === "wallet" && selectedUser) {
        onUserSelect(null);
      }
    }
  };

  // Validate amount in real-time
  const validateAmount = (amount) => {
    const numAmount = parseFloat(amount);
    
    if (!amount || amount === "") {
      setAmountError("");
      return false;
    }
    
    if (isNaN(numAmount) || numAmount <= 0) {
      setAmountError("Please enter a valid amount greater than 0");
      return false;
    }
    
    if (numAmount > userBalance) {
      setAmountError(`Insufficient balance. Available: $${userBalance.toFixed(2)}`);
      return false;
    }
    
    setAmountError("");
    return true;
  };

  // Check if amount is close to balance (warning threshold)
  const isNearBalanceLimit = (amount) => {
    const numAmount = parseFloat(amount);
    return numAmount > 0 && (userBalance - numAmount) < (userBalance * 0.1); // Less than 10% remaining
  };

  // Validate user selection
  const validateUser = () => {
    if (!selectedUser) {
      setUserError("Please select a user to transfer to");
      return false;
    }
    setUserError("");
    return true;
  };

  const handleAmountChange = (e) => {
    let value = e.target.value;
    
    // Prevent negative values
    if (parseFloat(value) < 0) {
      value = "";
    }
    
    // Limit to 2 decimal places
    if (value.includes('.')) {
      const parts = value.split('.');
      if (parts[1] && parts[1].length > 2) {
        value = parts[0] + '.' + parts[1].slice(0, 2);
      }
    }
    
    setTransferAmount(value);
    validateAmount(value);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const isAmountValid = validateAmount(transferAmount);
    const isUserValid = validateUser();
    
    if (isAmountValid && isUserValid) {
      onSubmit();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9990] backdrop-blur-sm p-2 sm:p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl transform transition-all duration-300 scale-100">
        {/* Header with gradient and close button */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
          <h3 className="m-0 font-bold text-base sm:text-xl tracking-wide">💸 Transfer Balance</h3>
          <button 
            onClick={onClose} 
            className="bg-white bg-opacity-20 hover:bg-opacity-30 border-none text-white text-lg sm:text-xl cursor-pointer p-1.5 sm:p-2 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
          >
            ×
          </button>
        </div>
        
        <div className="p-4 sm:p-6">
          {/* Balance Display */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 sm:p-5 rounded-xl mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-0 border-l-4 border-blue-600 shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg sm:text-xl">💰</span>
              </div>
              <div className="text-center sm:text-left">
                <span className="font-semibold text-blue-700 text-sm sm:text-lg block">Available Balance</span>
                <div className="text-xs sm:text-sm text-blue-600">Ready to transfer</div>
              </div>
            </div>
            <span className="font-bold text-blue-700 text-xl sm:text-2xl">${userBalance.toFixed(2)}</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
            {/* User Search Section */}
            <div>
              <label className="block mb-2 sm:mb-3 font-semibold text-gray-700 text-sm sm:text-base flex items-center gap-2">
                <span className="text-blue-600">👤</span>
                Search Recipient
              </label>
              
              {/* Info Text */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                <p className="text-blue-800 text-xs sm:text-sm font-medium flex items-start sm:items-center gap-2">
                  <span className="flex-shrink-0">💳</span>
                  <span>Enter the recipient's wallet number in XXXX-XXXX format</span>
                </p>
              </div>

              <div className="relative">
                <div className="relative">
                  <input
                    type="text"
                    value={walletNumber}
                    onChange={handleWalletNumberChange}
                    placeholder="Enter wallet number (XXXX-XXXX)..."
                    className={`w-full p-3 sm:p-4 border-2 rounded-xl text-sm sm:text-base focus:outline-none focus:ring-4 transition-all duration-200 ${
                      walletSearchError 
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-100' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-blue-100'
                    }`}
                  />
                  {isSearchingWallet && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
                
                {userError && (
                  <p className="text-red-500 text-xs sm:text-sm mt-2 flex items-start gap-2">
                    <span className="flex-shrink-0">⚠️</span>
                    <span>{userError}</span>
                  </p>
                )}

                {walletSearchError && (
                  <p className="text-red-500 text-xs sm:text-sm mt-2 flex items-start gap-2">
                    <span className="flex-shrink-0">⚠️</span>
                    <span>{walletSearchError}</span>
                  </p>
                )}
              </div>
              
              {selectedUser && (
                <div className="mt-3 sm:mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-3 sm:p-5 shadow-sm">
                  <div className="flex items-center gap-3 sm:gap-4">
                    {selectedUser.picture ? (
                      <img 
                        src={`data:image/jpeg;base64,${selectedUser.picture}`} 
                        alt="Profile" 
                        className="w-10 h-10 sm:w-14 sm:h-14 rounded-full object-cover border-2 sm:border-3 border-green-400 flex-shrink-0"
                      />
                    ) : (
                      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-xl flex-shrink-0">
                        {selectedUser.display_name ? selectedUser.display_name.charAt(0).toUpperCase() : '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-gray-800 text-sm sm:text-lg truncate">{selectedUser.full_name || 'Unknown User'}</div>
                      <div className="font-medium text-green-600 text-xs sm:text-base truncate">@{selectedUser.display_name || 'unknown'}</div>
                      {selectedUser.wallet_number && (
                        <div className="font-medium text-blue-600 text-xs sm:text-sm">💳 {selectedUser.wallet_number}</div>
                      )}
                    </div>
                    <div className="text-green-600 text-xl sm:text-2xl flex-shrink-0">✅</div>
                  </div>
                  <p className="text-green-700 text-xs sm:text-sm mt-2 sm:mt-3 flex items-center gap-2 font-medium">
                    <span>✅</span>
                    <span>Wallet number verified - Recipient found</span>
                  </p>
                </div>
              )}
            </div>

            {/* Amount Section */}
            <div>
              <label className="block mb-2 sm:mb-3 font-semibold text-gray-700 text-sm sm:text-base flex items-center gap-2">
                <span className="text-green-600">💵</span>
                Transfer Amount ($)
              </label>
              <input
                type="number"
                value={transferAmount}
                onChange={handleAmountChange}
                placeholder="Enter amount to transfer"
                min="0.01"
                step="0.01"
                max={userBalance}
                className={`w-full p-3 sm:p-4 border-2 rounded-xl text-sm sm:text-base focus:outline-none focus:ring-4 transition-all duration-200 ${
                  amountError 
                    ? 'border-red-400 focus:border-red-500 focus:ring-red-100' 
                    : 'border-gray-200 focus:border-blue-500 focus:ring-blue-100'
                }`}
                required
              />
              {amountError && (
                <p className="text-red-500 text-xs sm:text-sm mt-2 flex items-start gap-2">
                  <span className="flex-shrink-0">⚠️</span>
                  <span>{amountError}</span>
                </p>
              )}
              {transferAmount && !amountError && parseFloat(transferAmount) > 0 && (
                <>
                  <p className="text-green-600 text-xs sm:text-sm mt-2 flex items-start gap-2">
                    <span className="flex-shrink-0">✅</span>
                    <span>Valid amount. Remaining balance: ${(userBalance - parseFloat(transferAmount)).toFixed(2)}</span>
                  </p>
                  {isNearBalanceLimit(transferAmount) && (
                    <p className="text-yellow-600 text-xs sm:text-sm mt-1 flex items-start gap-2 bg-yellow-50 p-2 sm:p-3 rounded-lg border border-yellow-200">
                      <span className="flex-shrink-0">⚠️</span>
                      <span>Warning: This will leave you with a low balance</span>
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Description Section */}
            <div>
              <label className="block mb-2 sm:mb-3 font-semibold text-gray-700 text-sm sm:text-base flex items-center gap-2">
                <span className="text-purple-600">📝</span>
                Description (Optional)
              </label>
              <textarea
                value={transferDescription}
                onChange={(e) => setTransferDescription(e.target.value)}
                placeholder="Enter transfer description..."
                className="w-full p-3 sm:p-4 border-2 border-gray-200 rounded-xl text-sm sm:text-base focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 resize-y min-h-[80px] sm:min-h-[100px]"
                rows="3"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 justify-end pt-4 sm:pt-6 border-t border-gray-200">
              <button 
                type="button" 
                onClick={onClose} 
                className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 border-2 border-gray-300 rounded-xl font-semibold text-sm sm:text-base cursor-pointer transition-all duration-200 sm:min-w-[140px] bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-4 focus:ring-gray-200"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 border-none rounded-xl font-semibold text-sm sm:text-base cursor-pointer transition-all duration-200 sm:min-w-[140px] bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-blue-300 shadow-lg"
                disabled={
                  !selectedUser || 
                  !transferAmount || 
                  parseFloat(transferAmount) <= 0 || 
                  parseFloat(transferAmount) > userBalance ||
                  amountError ||
                  userError ||
                  walletSearchError ||
                  isSearchingWallet
                }
              >
                Review Transfer →
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TransferModal; 